/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * The single-program workspace. The same object is shown through its whole
 * lifecycle; the body + the action bar adapt to its status. Transitions call the
 * service verbs (the DB is the real gate) and reload.
 */
import { useCallback, useEffect, useState } from "react";
import { useWorkspaceAdmin } from "../../../shared/hooks/useWorkspaceAdmin.js";
import { useSession } from "../../../shared/components/SessionProvider.jsx";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";
import {
  getProgram, deleteProgram, submitDraft, approveProgram, rejectProgram,
  markSent, markActive, beginScoring, completeProgram, getReviewConfig,
  resendProgram, sendWarning, sendReason, sendConfirmation, getIncentiveAccountId,
} from "../utils/incentiveService.js";
import { exportProgramPdf } from "../utils/incentivePdf.js";
import { describePayout, num } from "../utils/payout.js";
import { METRIC_LABEL, STATUS } from "../utils/constants.js";
import StatusBadge from "./StatusBadge.jsx";
import DraftEditor from "./DraftEditor.jsx";
import ReviewPanel from "./ReviewPanel.jsx";
import ScoringPanel from "./ScoringPanel.jsx";
import ProgressPanel from "./ProgressPanel.jsx";
import GoalScope from "./GoalScope.jsx";
import "./incentive.css";

function fmt(d) {
  return d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
}

// Statuses at or past the hand-over — where a delivery record is meaningful.
const SENT_ONWARDS = new Set([STATUS.SENT, STATUS.ACTIVE, STATUS.SCORING, STATUS.COMPLETED]);

// Read-only goals overview shown for non-draft states.
function GoalsSummary({ goals }) {
  if (goals.length === 0) return <p className="muted">No goals defined.</p>;
  return (
    <div>
      {goals.map((g, i) => (
        <div className="it-goal" key={g.id}>
          <strong>{i + 1}. {g.description || METRIC_LABEL[g.metric_type] || "Goal"}</strong>
          {g.metric_type === "custom" ? (
            <div className="muted" style={{ fontSize: 13, marginTop: 4, whiteSpace: "pre-wrap" }}>
              {g.custom_text || "Custom goal"} · {describePayout(g)}
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
              {METRIC_LABEL[g.metric_type]} · Target {num(g.target_value).toLocaleString()}{g.unit_label ? ` ${g.unit_label}` : ""} · {describePayout(g)}
            </div>
          )}
          <GoalScope goal={g} compact />
        </div>
      ))}
    </div>
  );
}

/**
 * Delivery state of the program packet (item 230). "Sent" used to mean a status
 * column had changed; it now means an email either did or did not leave, and
 * this says which — a rep who filled in a brand-manager address must never be
 * left assuming the distributor was contacted when nothing was.
 */
function DeliveryNotice({ program, onResend, busy }) {
  const confirmation = sendConfirmation(program);
  if (confirmation) {
    return (
      <div className="it-note it-note-ok">
        <strong>Delivered.</strong> {confirmation}
        <button className="link" style={{ padding: 0, marginLeft: 8 }} onClick={onResend} disabled={busy}>Send again</button>
      </div>
    );
  }
  if (program.send_status === "failed") {
    return (
      <div className="it-note it-note-bad">
        <strong>Not delivered.</strong>{" "}
        The packet did not reach the distributor — {sendReason(program.send_error)}.
        {!program.brand_manager_email && " Add a brand manager email on the program first."}
        <button className="link" style={{ padding: 0, marginLeft: 8 }} onClick={onResend} disabled={busy}>Try again</button>
      </div>
    );
  }
  return (
    <div className="it-note">
      <strong>Not emailed yet.</strong>{" "}
      {program.brand_manager_email
        ? <>Send the packet to {program.brand_manager_email}.</>
        : <>There&apos;s no brand manager email on this program, so there&apos;s nowhere to send it.</>}
      <button className="link" style={{ padding: 0, marginLeft: 8 }} onClick={onResend} disabled={busy || !program.brand_manager_email}>
        Email it now
      </button>
    </div>
  );
}

function ProgramFacts({ program }) {
  const states = Array.isArray(program.target_states) ? program.target_states : [];
  return (
    <div className="it-goal-grid" style={{ marginBottom: 16 }}>
      <div><div className="muted" style={{ fontSize: 12 }}>STATE(S)</div><strong>{states.length ? states.join(", ") : "—"}</strong></div>
      <div><div className="muted" style={{ fontSize: 12 }}>DISTRIBUTOR</div><strong>{program.distributor_name || "—"}</strong></div>
      <div><div className="muted" style={{ fontSize: 12 }}>BRAND MANAGER</div><strong>{program.brand_manager_name || "—"}{program.brand_manager_email ? ` · ${program.brand_manager_email}` : ""}</strong></div>
      <div><div className="muted" style={{ fontSize: 12 }}>WINDOW</div><strong>{fmt(program.start_date)} – {fmt(program.end_date)}</strong></div>
    </div>
  );
}

export default function ProgramDetailPage({ programId, onBack, onDistributorProgress }) {
  const { isWorkspaceAdmin } = useWorkspaceAdmin();
  const { user } = useSession();
  const [data, setData] = useState(null);
  const [account, setAccount] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [salesManagerId, setSalesManagerId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [flash, setFlash] = useState(null);
  const [warn, setWarn] = useState(null);

  // The Sales Manager (and any owner/admin) may approve/reject — mirrors the DB
  // gate (incentive_can_review). The DB is the real boundary; this picks the UI.
  const canReview = isWorkspaceAdmin || (!!user?.id && user.id === salesManagerId);

  const load = useCallback(async () => {
    setErr(null);
    try { setData(await getProgram(programId)); }
    catch (e) { setErr(e.message); }
    // Who reviews — best-effort and retried on every (re)load; on a transient
    // failure keep the previously-known value rather than blanking it (which
    // would wrongly hide Approve/Reject from the real Sales Manager).
    try { setSalesManagerId((await getReviewConfig()).salesManagerUserId); }
    catch { /* keep prior salesManagerId */ }
  }, [programId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getMyPrimaryAccount().then((a) => setAccount(a || null)).catch(() => {});
    getIncentiveAccountId().then(setAccountId).catch(() => {});
  }, []);

  async function run(fn, successMsg) {
    setBusy(true); setErr(null); setFlash(null); setWarn(null);
    try {
      const result = await fn();
      // Transitions that send mail return a delivery result. A send that failed
      // must be visible immediately, not discovered later by the distributor
      // asking why they never got anything (backlog item 204's lesson) — and the
      // success line is suppressed when it did, so the two never contradict.
      const delivery = result && typeof result === "object"
        ? ("sent" in result ? result : result.delivery)
        : null;
      const w = delivery ? sendWarning(delivery) : null;
      if (w) setWarn(w);
      else if (successMsg) setFlash(successMsg);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleResend() {
    setBusy(true); setErr(null); setFlash(null); setWarn(null);
    try {
      const result = await resendProgram(programId);
      const w = sendWarning(result);
      if (w) setWarn(w); else setFlash("Sent to the distributor.");
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleDelete() {
    if (!window.confirm("Delete this program and all its goals? This can't be undone.")) return;
    setBusy(true); setErr(null);
    try { await deleteProgram(programId); onBack(); }
    catch (e) { setErr(e.message); setBusy(false); }
  }

  async function exportPdf() {
    setErr(null);
    try {
      // Resolve the brand fresh if the async account load hasn't landed yet, so
      // the logo/colors are never missing due to a race with the click.
      const acct = account || (await getMyPrimaryAccount());
      await exportProgramPdf({
        program: data.program, goals: data.goals, scores: data.scores,
        accountName: acct?.name || "", logoUrl: acct?.logoUrl || "",
        primaryColor: acct?.primaryColor || "", secondaryColor: acct?.secondaryColor || "",
      });
    } catch (e) {
      console.error("[incentive] exportPdf", e);
      setErr(`Could not generate the PDF${e?.message ? `: ${e.message}` : "."}`);
    }
  }

  if (err && !data) return (
    <section className="settings-card wide">
      <button className="it-backlink" onClick={onBack}>← All programs</button>
      <p className="error">{err}</p>
    </section>
  );
  if (!data) return <section className="settings-card wide"><p className="muted">Loading…</p></section>;

  const { program, goals, scores, attachments } = data;
  const status = program.status;
  const canExport = goals.length > 0;
  const scored = status === STATUS.SCORING || status === STATUS.COMPLETED;

  return (
    <section className="settings-card wide">
      <button className="it-backlink" onClick={onBack}>← All programs</button>

      <div className="it-head">
        <div>
          <h1 style={{ marginBottom: 6 }}>{program.name}</h1>
          <StatusBadge status={status} />
        </div>
        <div className="it-actions">
          {program.distributor_name && status !== STATUS.DRAFT && status !== STATUS.PENDING_REVIEW && (
            <button className="secondary" onClick={() => onDistributorProgress?.(program.distributor_name)}>
              {program.distributor_name}&apos;s progress
            </button>
          )}
          <button className="secondary" onClick={exportPdf} disabled={!canExport}
                  title={canExport ? "" : "Add a goal first"}>
            {scored ? "Export final report (PDF)" : "Export PDF"}
          </button>
          {status === STATUS.DRAFT && (
            <button className="secondary danger" onClick={handleDelete} disabled={busy}>Delete</button>
          )}
        </div>
      </div>

      {program.review_notes && (status === STATUS.DRAFT || status === STATUS.APPROVED) && (
        <div className="it-note"><strong>Reviewer note:</strong> {program.review_notes}</div>
      )}
      {err && <p className="error">{err}</p>}
      {flash && <p className="muted" style={{ color: "var(--success)" }}>{flash}</p>}
      {warn && <div className="it-note it-note-bad">{warn}</div>}

      {/* Delivery state — from the moment the program is handed over. Not while
          it is still approved-but-unsent: the DB only accepts a send once the
          program has actually taken the → sent edge, so offering the button
          earlier would just hand the user a guaranteed failure. */}
      {SENT_ONWARDS.has(status) && (
        <DeliveryNotice program={program} onResend={handleResend} busy={busy} />
      )}

      {/* Body adapts to status */}
      {status === STATUS.DRAFT && (
        <DraftEditor program={program} goals={goals} onChanged={load} />
      )}

      {status === STATUS.PENDING_REVIEW && (
        <>
          <ProgramFacts program={program} />
          <GoalsSummary goals={goals} />
          <hr className="it-divider" />
          <ReviewPanel
            isManager={canReview}
            onApprove={(note) => run(() => approveProgram(programId, note), "Approved.")}
            onReject={(note) => run(() => rejectProgram(programId, note), "Returned to draft.")}
          />
        </>
      )}

      {(status === STATUS.APPROVED || status === STATUS.SENT || status === STATUS.ACTIVE) && (
        <>
          <ProgramFacts program={program} />
          <GoalsSummary goals={goals} />
          {/* Live progress the moment the program is out the door (item 231) —
              not something that starts existing only at scoring. */}
          {(status === STATUS.SENT || status === STATUS.ACTIVE) && (
            <>
              <hr className="it-divider" />
              <ProgressPanel program={program} goals={goals} scores={scores} accountId={accountId} />
            </>
          )}
        </>
      )}

      {(status === STATUS.SCORING || status === STATUS.COMPLETED) && (
        <>
          <ProgramFacts program={program} />
          <ScoringPanel
            program={program} goals={goals} scores={scores} attachments={attachments}
            readOnly={status === STATUS.COMPLETED}
            accountId={accountId}
            onChanged={load}
          />
        </>
      )}

      {/* Action bar — the lifecycle-advancing transition for this state */}
      <hr className="it-divider" />
      <div className="it-actions">
        {status === STATUS.DRAFT && (
          <button className="btn" onClick={() => run(() => submitDraft(programId), "Submitted.")} disabled={busy || goals.length === 0}
                  title={goals.length === 0 ? "Add at least one goal" : ""}>
            Submit program
          </button>
        )}
        {status === STATUS.APPROVED && (
          <button className="btn" onClick={() => run(() => markSent(programId), "Sent to the distributor.")} disabled={busy}>
            Send to distributor
          </button>
        )}
        {status === STATUS.SENT && (
          <button className="btn" onClick={() => run(() => markActive(programId), "Program is now active.")} disabled={busy}>Mark active</button>
        )}
        {status === STATUS.ACTIVE && (
          <button className="btn" onClick={() => run(() => beginScoring(programId), "Scoring started.")} disabled={busy}>Begin scoring</button>
        )}
        {status === STATUS.SCORING && (
          <button className="btn" onClick={() => run(() => completeProgram(programId), "Program completed.")} disabled={busy}>
            Complete & lock report
          </button>
        )}
        {status === STATUS.COMPLETED && (
          <span className="muted">This program is complete. Its scores are locked.</span>
        )}
        <span className="muted" style={{ alignSelf: "center", fontSize: 13 }}>
          {status === STATUS.DRAFT && (program.brand_manager_email
            ? `Submitting routes for manager review if your company requires it, otherwise emails the packet to ${program.brand_manager_email}.`
            : "Submitting routes for manager review if your company requires it. Add a brand manager email to have the packet emailed automatically.")}
        </span>
      </div>
    </section>
  );
}
